import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import toast, { Toaster } from 'react-hot-toast';
import Spinner from 'src/components/Spinner';
import { getClients } from 'src/redux/client/client.actions';
import { setLoading } from 'src/redux/user/user.actions';
import {
  bulkImportUsersAssignments,
  sendClientCredentials,
  sendReminderEmail
} from 'src/services/UserServices';
import { bulkImportQuestions } from 'src/services/AssessmentService';

const Onboarding = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);
  const clientsState = useSelector((state) => state.client.clients);
  const showLoading = useSelector((state) => state.user.loading);
  const [selectedClient, setSelectedClient] = useState('');
  const [usersFile, setUsersFile] = useState(null);
  const [questionsFile, setQuestionsFile] = useState(null);
  const [isUsersUploading, setIsUsersUploading] = useState(false);
  const [isQuestionsUploading, setIsQuestionsUploading] = useState(false);
  const [showCredentialsConfirm, setShowCredentialsConfirm] = useState(false);
  const [isSendingCredentials, setIsSendingCredentials] = useState(false);
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(getClients({ email: user.email }));
    dispatch(setLoading(false));
  }, []);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        const base64 = String(result).split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

  const validateClientSelection = () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return false;
    }
    return true;
  };

  const selectedClientName =
    clientsState.find((client) => String(client.id) === String(selectedClient))
      ?.name || '';

  const handleUsersUpload = async () => {
    if (!validateClientSelection()) return;
    if (!usersFile) {
      toast.error('Please select an xlsx file');
      return;
    }
    setIsUsersUploading(true);
    try {
      const fileBase64 = await fileToBase64(usersFile);
      await bulkImportUsersAssignments({
        fileName: usersFile.name,
        fileBase64,
        assignmentType: 'NESW',
        clientId: selectedClient
      });
      toast.success('Users uploaded successfully');
      setUsersFile(null);
    } catch (error) {
      toast.error('Users upload failed');
    } finally {
      setIsUsersUploading(false);
    }
  };

  const handleQuestionsUpload = async () => {
    if (!validateClientSelection()) return;
    if (!questionsFile) {
      toast.error('Please select an xlsx file');
      return;
    }
    setIsQuestionsUploading(true);
    try {
      const fileBase64 = await fileToBase64(questionsFile);
      await bulkImportQuestions({
        assignmentType: 'NESW',
        clientId: selectedClient,
        fileName: questionsFile.name,
        fileBase64
      });
      toast.success('Questions uploaded successfully');
      setQuestionsFile(null);
    } catch (error) {
      toast.error('Questions upload failed');
    } finally {
      setIsQuestionsUploading(false);
    }
  };

  const handleOpenCredentialsConfirm = () => {
    if (!validateClientSelection()) return;
    setShowCredentialsConfirm(true);
  };

  const handleCloseCredentialsConfirm = () => {
    if (isSendingCredentials) return;
    setShowCredentialsConfirm(false);
  };

  const handleSendCredentials = async () => {
    if (!validateClientSelection()) return;
    setIsSendingCredentials(true);
    try {
      await sendClientCredentials({ clientId: selectedClient });
      toast.success('Client credentials sent successfully');
      setShowCredentialsConfirm(false);
    } catch (error) {
      toast.error('Failed to send client credentials');
    } finally {
      setIsSendingCredentials(false);
    }
  };

  const handleOpenReminderConfirm = () => {
    if (!validateClientSelection()) return;
    setShowReminderConfirm(true);
  };

  const handleCloseReminderConfirm = () => {
    if (isSendingReminder) return;
    setShowReminderConfirm(false);
  };

  const handleSendReminder = async () => {
    if (!validateClientSelection()) return;
    setIsSendingReminder(true);
    try {
      await sendReminderEmail({ clientId: selectedClient });
      toast.success('Reminder email sent successfully');
      setShowReminderConfirm(false);
    } catch (error) {
      toast.error('Failed to send reminder email');
    } finally {
      setIsSendingReminder(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Onboarding</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Toaster position="top-center" reverseOrder={false} />
          <Spinner showLoading={showLoading} />
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography color="textPrimary" variant="h3">
                    Onboarding
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ margin: 5 }} htmlFor="Select Client">
                    Select Client
                  </InputLabel>
                  <Select
                    fullWidth
                    onChange={(event) => setSelectedClient(event.target.value)}
                    required
                    value={selectedClient}
                    variant="outlined"
                  >
                    {clientsState.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel style={{ marginBottom: 8 }}>
                    Upload Users (XLSX)
                  </InputLabel>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      rowGap: 1
                    }}
                  >
                    <input
                      accept=".xlsx"
                      id="users-xlsx-upload"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(event) => {
                        const file = event.target.files && event.target.files[0];
                        setUsersFile(file || null);
                      }}
                    />
                    <label htmlFor="users-xlsx-upload">
                      <Button color="primary" component="span" variant="outlined">
                        Choose Users File
                      </Button>
                    </label>
                    <Typography sx={{ px: 2 }} variant="body2">
                      {usersFile ? usersFile.name : 'No file selected'}
                    </Typography>
                    <Button
                      color="primary"
                      disabled={isUsersUploading}
                      variant="contained"
                      onClick={handleUsersUpload}
                    >
                      Upload Users
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel style={{ marginBottom: 8 }}>
                    Upload Questions (XLSX)
                  </InputLabel>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      rowGap: 1
                    }}
                  >
                    <input
                      accept=".xlsx"
                      id="questions-xlsx-upload"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(event) => {
                        const file = event.target.files && event.target.files[0];
                        setQuestionsFile(file || null);
                      }}
                    />
                    <label htmlFor="questions-xlsx-upload">
                      <Button color="primary" component="span" variant="outlined">
                        Choose Questions File
                      </Button>
                    </label>
                    <Typography sx={{ px: 2 }} variant="body2">
                      {questionsFile ? questionsFile.name : 'No file selected'}
                    </Typography>
                    <Button
                      color="primary"
                      disabled={isQuestionsUploading}
                      variant="contained"
                      onClick={handleQuestionsUpload}
                    >
                      Upload Questions
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2
                    }}
                  >
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={handleOpenCredentialsConfirm}
                    >
                      Send Credentials to Client
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={handleOpenReminderConfirm}
                    >
                      Send Reminder Email
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Dialog
            open={showCredentialsConfirm}
            onClose={handleCloseCredentialsConfirm}
          >
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Do you want to send client credentials?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                color="primary"
                disabled={isSendingCredentials}
                onClick={handleCloseCredentialsConfirm}
              >
                No
              </Button>
              <Button
                color="primary"
                disabled={isSendingCredentials}
                onClick={handleSendCredentials}
                variant="contained"
              >
                {isSendingCredentials ? (
                  <>
                    <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  'Yes'
                )}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={showReminderConfirm} onClose={handleCloseReminderConfirm}>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {`Do you want to send reminder email to ${
                  selectedClientName || 'the selected client'
                }?`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                color="primary"
                disabled={isSendingReminder}
                onClick={handleCloseReminderConfirm}
              >
                No
              </Button>
              <Button
                color="primary"
                disabled={isSendingReminder}
                onClick={handleSendReminder}
                variant="contained"
              >
                {isSendingReminder ? (
                  <>
                    <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  'Yes'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </>
  );
};

export default Onboarding;
